import {
  type UnprocessedEvent,
  type UnprocessedEventMissing,
  type UnprocessedEventResolution,
  type UnprocessedEventRetryBlock,
} from "@/lib/types";

export const MISSING_LABEL: Record<UnprocessedEventMissing, string> = {
  call_record: "Záznam hovoru v adminu",
  call_record_invalid: "Záznam hovoru v adminu – neplatná data, oprava v kódu",
  postcall_not_started:
    "Zpracování po hovoru nezačalo – do Emmy se po hovoru nic nezapsalo",
  postcall_not_reached:
    "Zpracování po hovoru nedoběhlo – do Emmy se po hovoru nic nezapsalo",
  postcall_write_uncertain:
    "Zápis do Emmy nedoběhl – zkontrolujte požadavek v Emmy",
  postcall_output_missing_emmy_done:
    "Přepis a shrnutí v adminu (požadavek v Emmy je)",
  postcall_output_missing_skipped:
    "Přepis a shrnutí v adminu (hovor se podle nastavení do Emmy nezapisuje)",
  postcall_output_missing_emmy_incomplete:
    "Přepis a shrnutí v adminu; zápis do Emmy se nedokončil – zkontrolujte Emmy",
  postcall_unknown:
    "Zpracování po hovoru nedoběhlo – zkontrolujte požadavek v Emmy",
  unknown_event: "Neznámá událost – předejte vývojářům",
};

const AGENT_TICKET_NOTE =
  " (požadavek vytvořený během hovoru je v Emmy bez shrnutí a přepisu)";
const RUNNING_NOTE = " – probíhá znovu";

const MISSING_WITH_AGENT_TICKET_NOTE: ReadonlySet<UnprocessedEventMissing> =
  new Set(["postcall_not_started", "postcall_not_reached"]);

export const missingText = (event: UnprocessedEvent): string => {
  let text = MISSING_LABEL[event.missing];
  if (
    event.emmyTicketFromAgent &&
    MISSING_WITH_AGENT_TICKET_NOTE.has(event.missing)
  ) {
    text += AGENT_TICKET_NOTE;
  }
  if (event.executionStatus === "running") {
    text += RUNNING_NOTE;
  }
  return text;
};

export const RETRY_BLOCK_LABEL: Record<UnprocessedEventRetryBlock, string> = {
  call_row_missing: "Nejdřív obnovte záznam hovoru",
  before_protection: "Starší než ochrana zápisu – zkontrolujte ručně",
  not_redrivable:
    "Už nejde zpracovat znovu (starší než 14 dní) – zkontrolujte ručně",
  running: "Probíhá",
  write_uncertain: "Zkontrolujte požadavek v Emmy a doplňte ho ručně",
};

export const RESOLUTION_LABEL: Record<UnprocessedEventResolution, string> = {
  reprocessed: "znovu zpracováno",
  manual: "ručně",
  moved_to_call_problem: "přesunuto do Problémy u hovoru",
};

/** Reasons the API gives with a 409 on retry / resolve. */
export const REFUSAL_LABEL: Record<string, string> = {
  stale: "Mezitím se změnilo – seznam je obnovený",
  running: "Už probíhá",
  already_claimed: "Už se zpracovává (zkuste to za 15 minut)",
  call_row_missing: RETRY_BLOCK_LABEL.call_row_missing,
  before_protection: RETRY_BLOCK_LABEL.before_protection,
  not_redrivable: RETRY_BLOCK_LABEL.not_redrivable,
  not_retryable: "Tuto událost nejde zpracovat znovu",
  already_resolved: "Už je vyřešeno",
  not_found: "Událost už neexistuje",
  aws_failed: "Volání AWS selhalo",
};

const ERROR_CODE_LABEL: Record<string, string> = {
  "Sandbox.Timedout": "Vypršel čas",
  "States.Timeout": "Vypršel čas",
  "Lambda.Unknown": "Vypršel čas",
  ThrottlingException: "Omezení AWS",
  "SFN.ThrottlingException": "Omezení AWS",
  TooManyRequestsException: "Omezení AWS",
  "Lambda.TooManyRequestsException": "Omezení AWS",
  "org.postgresql.util.PSQLException": "Databáze nedostupná",
  "java.sql.SQLTransientConnectionException": "Databáze nedostupná",
  "java.sql.SQLException": "Databáze nedostupná",
  "java.net.SocketTimeoutException": "Databáze nedostupná",
  "States.Runtime": "Chyba definice zpracování",
  "States.DataLimitExceeded": "Chyba definice zpracování",
  "cz.sestraemmy.voice.impl.emmy.EmmyWriteIncomplete":
    "Zápis do Emmy nedokončen",
  EmmyWriteFailed: "Zápis do Emmy selhal",
  invalid_payload: "Neplatná data",
  "java.lang.RuntimeException": "Chyba zpracování",
  "java.lang.IllegalStateException": "Chyba zpracování",
};

export const errorCodeText = (code: string): string =>
  ERROR_CODE_LABEL[code] ?? code;

export const SCAN_STALE_AFTER_MS = 60 * 60 * 1000;

export const isScanStale = (lastScanAt: string | null, now: number): boolean =>
  lastScanAt === null ||
  now - new Date(lastScanAt).getTime() > SCAN_STALE_AFTER_MS;
